import { Modal, Text } from '@ui/themed';
import { t } from 'i18next';
import type { PropsWithChildren, ReactNode } from 'react';

export type ConfirmModalProps = PropsWithChildren<{
  isOpen: boolean;
  onClose: () => void;
  title: string;
  /** Body copy. Use `children` for anything richer. */
  message?: ReactNode;
  confirmText: string;
  onConfirm: () => void;
  /** Spins the confirm, disables both actions, and blocks dismissal while in flight. */
  loading?: boolean;
  /** @default true */
  danger?: boolean;
}>;

/**
 * The confirm-then-act dialog. Destructive by default — pass `danger={false}` for a
 * merely irreversible one.
 */
export function ConfirmModal({
  isOpen,
  onClose,
  title,
  message,
  confirmText,
  onConfirm,
  loading = false,
  danger = true,
  children,
}: ConfirmModalProps) {
  // Backdrop, escape and cancel all route through here, so one guard covers them.
  const handleClose = () => {
    if (loading) {
      return;
    }
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      primaryAction={{
        text: confirmText,
        onClick: onConfirm,
        danger,
        disabled: loading,
        loading,
      }}
      secondaryAction={{
        text: t('buttons:cancel'),
        onClick: handleClose,
        disabled: loading,
      }}
    >
      <Modal.Header>{title}</Modal.Header>
      <Modal.Body>
        <div className="flex flex-col gap-2">
          {message != null && <Text variant="secondary">{message}</Text>}
          {children}
        </div>
      </Modal.Body>
    </Modal>
  );
}
